import { useState } from 'react';
import { Mail, FileText } from 'lucide-react';
import { useNewsletterArchive } from '../hooks/useNewsletterArchive';
import NewsletterModal from '../components/NewsletterModal';
import CategoryBadge from '../components/CategoryBadge';
import type { NewsletterArchiveItem, NewsletterDto } from '@shared/types';

export default function Newsletters() {
  const { data, isLoading, error } = useNewsletterArchive();
  const [opened, setOpened] = useState<NewsletterArchiveItem | null>(null);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal/20 border-t-teal" />
      </div>
    );
  }

  if (error) {
    return <div className="card p-8 text-center text-danger">Chyba: {(error as Error).message}</div>;
  }

  const items = data ?? [];

  if (items.length === 0) {
    return (
      <div>
        <h1 className="mb-2 font-heading text-2xl font-semibold text-dark">Newslettery</h1>
        <p className="mb-6 text-sm text-gray-600">
          Tady budou všechny newslettery, které vygeneruješ z archivu. Zatím
          žádný nemáš — otevři <strong>Archív</strong>, zaškrtni 2–5 zpráv
          a klikni „Vytvořit newsletter".
        </p>
        <div className="card p-8 text-center text-sm text-gray-500">
          📭 Prázdný archív.
        </div>
      </div>
    );
  }

  // Adapt to NewsletterDto shape pro reuse modalu
  const modalData: NewsletterDto | null = opened
    ? {
        bodyCs: opened.bodyCs,
        bodyEn: opened.bodyEn,
        bodyPl: opened.bodyPl,
        bodyVi: opened.bodyVi,
        generatedAt: opened.generatedAt,
        cached: true,
      }
    : null;

  return (
    <div>
      <h1 className="mb-2 font-heading text-2xl font-semibold text-dark">Newslettery</h1>
      <p className="mb-6 text-sm text-gray-600">
        Historie vygenerovaných newsletterů. {items.length}{' '}
        {items.length === 1 ? 'newsletter' : items.length < 5 ? 'newslettery' : 'newsletterů'}.
        Klikni pro otevření — můžeš si kopírovat libovolnou jazykovou verzi.
      </p>

      <div className="space-y-3">
        {items.map((n) => (
          <NewsletterRow key={n.id} newsletter={n} onClick={() => setOpened(n)} />
        ))}
      </div>

      {opened && (
        <NewsletterModal
          itemCount={opened.items.length}
          loading={false}
          error={null}
          data={modalData}
          onClose={() => setOpened(null)}
        />
      )}
    </div>
  );
}

function NewsletterRow({
  newsletter,
  onClick,
}: {
  newsletter: NewsletterArchiveItem;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="card group block w-full p-5 text-left transition-colors hover:bg-gray-50"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-teal" />
          <span className="font-heading text-base font-semibold text-dark">
            {formatDate(newsletter.generatedAt)}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <FileText className="h-3.5 w-3.5" />
          {newsletter.items.length}{' '}
          {newsletter.items.length === 1 ? 'zpráva' : newsletter.items.length < 5 ? 'zprávy' : 'zpráv'}
        </div>
      </div>

      <p className="mb-3 text-sm text-gray-700 line-clamp-2">
        {snippet(newsletter.bodyCs, 240)}
      </p>

      <div className="space-y-1">
        {newsletter.items.map((it) => (
          <div key={it.id} className="flex items-start gap-2 text-xs text-gray-600">
            <CategoryBadge category={it.category} />
            <span className="flex-1 truncate">{it.titleCz}</span>
            <span className="text-gray-400">{it.sourceName}</span>
          </div>
        ))}
      </div>
    </button>
  );
}

function snippet(text: string, max: number): string {
  // Strip first line if it looks like a greeting, otherwise take first ~max chars
  const flat = text.replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return flat;
  return flat.slice(0, max).trim() + '…';
}

function formatDate(unixSec: number): string {
  const d = new Date(unixSec * 1000);
  return d.toLocaleString('cs-CZ', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
